import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";

import {AppContext} from "../../HomePage";

const BASE = 'https://api.pricerest.com';

// One generator for every language, so adding an endpoint yields six correct
// samples instead of six hand-written ones that can drift apart. Every request
// the public API exposes is a GET carrying the same header.
const snippets = (url) => [
    {lang: 'cURL', code:
`curl -H "Authorization: Token YOUR_API_KEY" \\
  "${url}"`},
    {lang: 'Python', code:
`import requests

response = requests.get(
    "${url}",
    headers={"Authorization": "Token YOUR_API_KEY"},
)
print(response.json())`},
    {lang: 'Java', code:
`HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("${url}"))
    .header("Authorization", "Token YOUR_API_KEY")
    .GET()
    .build();

HttpResponse<String> response = HttpClient.newHttpClient()
    .send(request, HttpResponse.BodyHandlers.ofString());
System.out.println(response.body());`},
    {lang: 'C#', code:
`using var client = new HttpClient();
client.DefaultRequestHeaders.Add("Authorization", "Token YOUR_API_KEY");

var response = await client.GetStringAsync("${url}");
Console.WriteLine(response);`},
    {lang: 'PHP', code:
`$ch = curl_init("${url}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Token YOUR_API_KEY"]);

$response = curl_exec($ch);
curl_close($ch);
echo $response;`},
    {lang: 'Node.js', code:
`const response = await fetch("${url}", {
  headers: { Authorization: "Token YOUR_API_KEY" },
});

console.log(await response.json());`},
];

const CAMPAIGNS_RESPONSE = `{
  "campaigns": [
    { "campaign_id": "6410b1c2e4b0a1f2c3d4e5f6", "campaign_name": "Brand Protection" }
  ]
}`;

const OVERVIEW_RESPONSE = `{
  "campaign_id": "6410b1c2e4b0a1f2c3d4e5f6",
  "total_products": 1240,
  "total_active_products": 1198,
  "total_inactive_products": 42,
  "total_products_without_competitors": 87,
  "total_cheapest": 512,
  "total_same": 233,
  "total_higher": 366,
  "total_highest": 129,
  "total_violated_mrp": 18
}`;

const PRODUCTS_RESPONSE = `{
  "campaign_id": "6410b1c2e4b0a1f2c3d4e5f6",
  "campaign_name": "Brand Protection",
  "page_info": {
    "total_product_count": 1240,
    "total_page": 13,
    "current_page": 1,
    "product_count_per_page": 100
  },
  "products": [
    {
      "id": "6410b1c2e4b0a1f2c3d4e601",
      "name": "Example Product 1L",
      "image": "https://cdn.example.com/p/1.jpg",
      "product_code": "EX-1001",
      "brand": "Example",
      "category": "Home",
      "my_product": {
        "market_place": "mystore.com",
        "name": "mystore.com",
        "url": "https://www.mystore.com/p/1",
        "price": 249.9,
        "currency": "TRY",
        "stock": 1,
        "date": "2026-08-18T06:12:44"
      },
      "sellers": [
        {
          "market_place": "rival.com",
          "name": "rival.com",
          "url": "https://www.rival.com/p/1",
          "price": 239.0,
          "currency": "TRY",
          "stock": 1,
          "date": "2026-08-18T06:10:02"
        }
      ]
    }
  ]
}`;

const PRICE_SUGGESTIONS_RESPONSE = `{
  "campaign_id": "6410b1c2e4b0a1f2c3d4e5f6",
  "campaign_name": "Brand Protection",
  "campaign_strategy": {
    "reduction_type": "ratio",
    "reduction_value": -2.0,
    "protection_strategy": "min_value",
    "no_competitor_strategy": false,
    "is_active": true,
    "is_auto_update": true,
    "guarantee_buybox": false,
    "buybox_step": null,
    "buybox_step_type": "amount"
  },
  "page_info": {
    "total_product_count": 318,
    "total_page": 4,
    "current_page": 1,
    "product_count_per_page": 100
  },
  "products": [
    {
      "id": "6410b1c2e4b0a1f2c3d4e601",
      "name": "Example Product 1L",
      "image": "https://cdn.example.com/p/1.jpg",
      "product_code": "EX-1001",
      "brand": "Example",
      "category": "Home",
      "url": "https://www.mystore.com/p/1",
      "current_price": 249.9,
      "sale_price": 279.9,
      "currency": "TRY",
      "stock": 12,
      "cheapest_competitor_price": 239.0,
      "suggested_price": 234.22,
      "target_price": 234.22,
      "is_auto_update_paused": false,
      "strategy": {
        "source": "template",
        "template": { "id": "6501c3d4e4b0a1f2c3d4e777", "name": "Aggressive -2%" },
        "min_value": 220.0,
        "max_value": 320.0,
        "optimum_value": 250.0,
        "rule": {
          "reduction_type": "ratio",
          "reduction_value": -2.0,
          "protection_strategy": "min_value",
          "no_competitor_strategy": false,
          "is_active": true,
          "is_auto_update": true,
          "guarantee_buybox": false,
          "buybox_step": null,
          "buybox_step_type": "amount"
        }
      }
    }
  ]
}`;

// The section list drives both the left navigation and the content pane, so the
// two cannot drift apart. `nameKey` is the short human label; the path stays in
// the heading because that is what the reader actually types.
const sections = (t) => [
    {
        id: 'auth',
        icon: 'pi-key',
        name: t('api_doc.auth_title'),
        title: t('api_doc.auth_title'),
        body: t('api_doc.auth_body'),
        requests: snippets(`${BASE}/api/campaigns/`),
    },
    {
        id: 'campaigns',
        name: t('api_doc.campaigns.name'),
        method: 'GET',
        path: '/api/campaigns/',
        body: t('api_doc.campaigns.description'),
        params: [],
        requests: snippets(`${BASE}/api/campaigns/`),
        response: CAMPAIGNS_RESPONSE,
    },
    {
        id: 'overview',
        name: t('api_doc.overview.name'),
        method: 'GET',
        path: '/api/overview/',
        body: t('api_doc.overview.description'),
        params: [{name: 'campaign_id', required: true, description: t('api_doc.overview.campaign_id')}],
        requests: snippets(`${BASE}/api/overview/?campaign_id=CAMPAIGN_ID`),
        response: OVERVIEW_RESPONSE,
    },
    {
        id: 'products',
        name: t('api_doc.products.name'),
        method: 'GET',
        path: '/api/products/',
        body: t('api_doc.products.description'),
        params: [
            {name: 'campaign_id', required: true, description: t('api_doc.products.campaign_id')},
            {name: 'page', required: false, description: t('api_doc.products.page')},
        ],
        requests: snippets(`${BASE}/api/products/?campaign_id=CAMPAIGN_ID&page=1`),
        response: PRODUCTS_RESPONSE,
    },
    {
        id: 'price_suggestions',
        name: t('api_doc.price_suggestions.name'),
        method: 'GET',
        path: '/api/price_suggestions/',
        body: t('api_doc.price_suggestions.description'),
        params: [
            {name: 'campaign_id', required: true, description: t('api_doc.price_suggestions.campaign_id')},
            {name: 'page', required: false, description: t('api_doc.price_suggestions.page')},
        ],
        fields: [
            'suggested_price', 'target_price', 'campaign_strategy', 'strategy.source',
            'strategy.template', 'strategy.min_value', 'strategy.rule',
        ].map(name => ({name, description: t('api_doc.price_suggestions.fields.' + name.replace('.', '_'))})),
        requests: snippets(`${BASE}/api/price_suggestions/?campaign_id=CAMPAIGN_ID&page=1`),
        response: PRICE_SUGGESTIONS_RESPONSE,
    },
    {
        id: 'errors',
        icon: 'pi-exclamation-triangle',
        name: t('api_doc.errors_title'),
        title: t('api_doc.errors_title'),
        errors: [
            {status: '401', meaning: t('api_doc.error_401')},
            {status: '403', meaning: t('api_doc.error_403')},
            {status: '404', meaning: t('api_doc.error_404')},
        ],
    },
];

const CodeBlock = ({label, code, onCopy, copyLabel, children}) => (
    <div className="api-code">
        <div className="api-code-head">
            {children || <span className="api-code-label">{label}</span>}
            {onCopy && <button type="button" className="api-code-copy" onClick={() => onCopy(code)}>
                <i className="pi pi-copy" style={{fontSize: '12px', marginRight: '4px'}}/>
                {copyLabel}
            </button>}
        </div>
        <pre>{code}</pre>
    </div>
);

const RequestBlock = ({requests, onCopy, copyLabel}) => {
    const [lang, setLang] = useState(requests[0].lang);
    const active = requests.find(r => r.lang === lang) || requests[0];

    return <CodeBlock code={active.code} onCopy={onCopy} copyLabel={copyLabel}>
        <div className="api-langs">
            {requests.map(r => (
                <button key={r.lang} type="button"
                        className={'api-lang' + (r.lang === lang ? ' is-active' : '')}
                        onClick={() => setLang(r.lang)}>
                    {r.lang}
                </button>
            ))}
        </div>
    </CodeBlock>;
};

// On paper there are no language tabs to switch, and six samples per endpoint
// would bury the reference, so the printed copy shows the cURL sample alone.
const PrintedRequest = ({requests, t}) =>
    <CodeBlock label={t('api_doc.request') + ' (' + requests[0].lang + ')'} code={requests[0].code}/>;

const Section = ({section, t, onCopy, printed}) => {
    const copyLabel = t('api_doc.copy');

    return <div className="api-pane">
        {section.method
            ? <div className="api-ep-head">
                  <span className="api-method">{section.method}</span>
                  <span className="api-path">{section.path}</span>
              </div>
            : <p className="acc-section-title">
                  <i className={'pi ' + section.icon + ' api-title-icon'}/>
                  {section.title}
              </p>}

        {section.body && <p className="acc-section-sub">{section.body}</p>}

        {section.params && section.params.length > 0 && (
            <table className="api-table">
                <thead>
                    <tr>
                        <th>{t('api_doc.param')}</th>
                        <th>{t('api_doc.required')}</th>
                        <th>{t('api_doc.description')}</th>
                    </tr>
                </thead>
                <tbody>
                    {section.params.map(p => (
                        <tr key={p.name}>
                            <td><code>{p.name}</code></td>
                            <td>
                                {p.required
                                    ? <span className="api-req">{t('api_doc.yes')}</span>
                                    : <span className="api-opt">{t('api_doc.no')}</span>}
                            </td>
                            <td>{p.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}

        {section.fields && section.fields.length > 0 && (
            <table className="api-table">
                <thead>
                    <tr>
                        <th>{t('api_doc.field')}</th>
                        <th>{t('api_doc.description')}</th>
                    </tr>
                </thead>
                <tbody>
                    {section.fields.map(f => (
                        <tr key={f.name}>
                            <td><code>{f.name}</code></td>
                            <td>{f.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}

        {section.requests && (printed
            ? <PrintedRequest requests={section.requests} t={t}/>
            : <RequestBlock requests={section.requests} onCopy={onCopy} copyLabel={copyLabel}/>)}

        {section.response && (
            <CodeBlock label={t('api_doc.response')} code={section.response}
                       onCopy={printed ? null : onCopy} copyLabel={copyLabel}/>
        )}

        {section.errors && (
            <table className="api-table">
                <thead>
                    <tr>
                        <th>{t('api_doc.status')}</th>
                        <th>{t('api_doc.meaning')}</th>
                    </tr>
                </thead>
                <tbody>
                    {section.errors.map(e => (
                        <tr key={e.status}>
                            <td><code>{e.status}</code></td>
                            <td>{e.meaning}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>;
};

const ApiDocDialog = ({visible, onHide}) => {
    const {t, toast} = React.useContext(AppContext);

    const all = sections(t);
    const [activeId, setActiveId] = useState(all[0].id);
    const active = all.find(s => s.id === activeId) || all[0];

    // "Download PDF" goes through the browser's print dialog ("Save as PDF"):
    // every section is rendered once into a print-only container on <body>, and
    // the print stylesheet hides everything else. The page title becomes the
    // suggested file name while the dialog is open.
    const [printing, setPrinting] = useState(false);
    useEffect(() => {
        if (!printing) {
            return undefined;
        }
        const previousTitle = document.title;
        const done = () => setPrinting(false);
        document.title = t('api_doc.pdf_file_name');
        window.addEventListener('afterprint', done);
        window.print();
        return () => {
            window.removeEventListener('afterprint', done);
            document.title = previousTitle;
        };
    }, [printing, t]);

    const onCopy = (text) => {
        navigator.clipboard.writeText(text);
        toast.current.show({
            severity: 'success',
            summary: t('api_doc.copied'),
            detail: t('message_detail.successful')
        });
    };

    const header = <div className="api-doc-header">
        <span>{t('api_doc.title')}</span>
        <Button type="button" icon="pi pi-file-pdf" label={t('api_doc.download_pdf')}
                className="p-button-outlined p-button-sm" disabled={printing}
                onClick={() => setPrinting(true)}/>
    </div>;

    return <>
    <Dialog visible={visible} onHide={onHide} modal
                   header={header}
                   className="api-doc-dialog"
                   style={{width: '92vw', maxWidth: '1080px'}}
                   breakpoints={{'960px': '96vw'}}>
        <div className="api-doc-body">
            <nav className="api-doc-nav">
                {all.map(s => (
                    <button key={s.id} type="button"
                            className={'api-doc-navitem' + (s.id === activeId ? ' is-active' : '')}
                            onClick={() => setActiveId(s.id)}>
                        {s.method
                            ? <span className="api-nav-method">{s.method}</span>
                            : <i className={'pi ' + s.icon + ' api-nav-icon'}/>}
                        <span>{s.name}</span>
                    </button>
                ))}
            </nav>
            <div className="api-doc-content">
                <Section section={active} t={t} onCopy={onCopy}/>
            </div>
        </div>
    </Dialog>
    {printing && ReactDOM.createPortal(
        <div className="api-doc-print">
            <h1>{t('api_doc.title')}</h1>
            <p className="api-doc-print-intro">{t('api_doc.intro')}</p>
            <p className="api-doc-print-intro">{t('api_doc.base_url')}: <code>{BASE}</code></p>
            {all.map(s => (
                <div key={s.id} className="api-doc-print-section">
                    <Section section={s} t={t} printed/>
                </div>
            ))}
        </div>,
        document.body)}
    </>;
};

export default ApiDocDialog;
